import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'
import { type JSX, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AvatarStack } from '~/features/avatar'
import { GroupMutators } from '~/mutators/groups'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormErrorAlert,
  FormFieldInput,
  Icon,
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  processFormError,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/groups/')({
  component: RouteComponent,
})

function RouteComponent(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()
  const [newGroupDialogOpen, setNewGroupDialogOpen] = useState(false)

  const search = location.search as Record<string, unknown>
  const query = (search.query as string) || ''
  const filters = (search.filters as Record<string, unknown>) || {}
  const archived = (filters.archived as boolean | undefined) ?? false

  const { data: groups } = useQuery(trpc.userGroups.getAll.queryOptions({ archived }))

  const filteredGroups = useMemo(() => {
    if (!groups || !query) return groups
    const q = query.toLowerCase()
    return groups.filter((group) => group.name.toLowerCase().includes(q))
  }, [groups, query])

  useSettingTitle(['Groups'])

  return (
    <>
      <SettingsActions>
        <Button onClick={() => setNewGroupDialogOpen(true)}>New Group</Button>
      </SettingsActions>
      <SettingsContent spacing="sm" className="w-full overflow-y-auto">
        <header className="flex items-center justify-between gap-4">
          <Search
            placeholder="Search groups"
            onSearch={(value) =>
              navigate({
                to: location.pathname,
                search: {
                  ...(location.search as Record<string, unknown>),
                  query: value || undefined,
                  page: 1,
                },
                replace: true,
              })
            }
            defaultValue={query}
          />
          <Select
            value={archived ? 'archived' : 'active'}
            onValueChange={(value) =>
              navigate({
                to: location.pathname,
                search: {
                  ...(location.search as Record<string, unknown>),
                  filters: {
                    ...(((location.search as Record<string, unknown>).filters as Record<
                      string,
                      unknown
                    >) || {}),
                    archived: value === 'archived' ? true : undefined,
                  },
                  page: 1,
                },
                replace: true,
              })
            }
          >
            <SelectTrigger size="sm-wd" className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </header>

        <div className="grid gap-3">
          {filteredGroups?.length === 0 && (
            <div className="text-muted-foreground py-12 text-center">
              {query
                ? 'No groups match your search.'
                : archived
                  ? 'No archived groups.'
                  : 'No groups yet.'}
            </div>
          )}
          {filteredGroups?.map((group) => (
            <button
              onClick={() =>
                navigate({
                  to: `/$alias/settings/groups/${group.id}`,
                  search: location.search,
                })
              }
              key={group.id}
              className="hover:bg-accent flex items-center justify-between gap-4 rounded-md border p-4 text-left"
            >
              <div className="shrink grow">
                <p className="flex items-center gap-2 text-lg font-semibold">
                  {group.name}
                  {group.archivedAt && (
                    <span className="text-muted-foreground text-sm font-normal">
                      (Archived)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <AvatarStack
                  members={group.memberPreview || []}
                  total={group.memberCount || 0}
                />
                <span className="text-accent-foreground text-sm">
                  {group.memberCount || 0} {pluralize('member', group.memberCount || 0)}
                </span>
              </div>
            </button>
          ))}
        </div>

        <NewGroupDialog
          open={newGroupDialogOpen}
          onOpenChange={setNewGroupDialogOpen}
          onSuccess={(groupId) => {
            navigate({
              to: `/$alias/settings/groups/${groupId}`,
              search: location.search,
            })
          }}
        />
      </SettingsContent>
    </>
  )
}

const newGroupSchema = z.object({
  name: z.string().min(1, 'Please enter a group name'),
})

interface NewGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (groupId: string) => void
}

function NewGroupDialog({ open, onOpenChange, onSuccess }: NewGroupDialogProps) {
  const { create, isPending } = GroupMutators.useCreate({
    onSuccess: (groupId) => {
      form.reset()
      onOpenChange(false)
      onSuccess?.(groupId)
    },
  })

  const form = useForm({
    defaultValues: { name: '' },
    resolver: zodResolver(newGroupSchema),
  })

  const handleSubmit = async (values: z.infer<typeof newGroupSchema>) => {
    try {
      await create(values)
    } catch (e) {
      processFormError(form.setError, e, {
        genericMessage:
          'There was a problem creating your group. Please try changing the name and try again.',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] p-20">
        <DialogHeader>
          <DialogTitle size="lg">Create a Group</DialogTitle>
          <DialogDescription>
            Groups allow you to organize members by function.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-3">
            <FormErrorAlert />
            <FormFieldInput
              control={form.control}
              name="name"
              size="lg"
              placeholder="Group name (e.g., Customer Care)"
            />
            <Button
              type="submit"
              size="lg"
              loading={isPending}
              loadingLabel="Creating..."
            >
              Create Group <Icon name="arrow-right" size={20} />
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
